import CloseIcon from '@material-ui/icons/Close';
import useAutocomplete from '@material-ui/lab/useAutocomplete';
import { createStyles, makeStyles } from '@material-ui/styles';
import clsx from 'clsx';
import React from 'react';
import { JupiterTheme } from '../../theme/types';
import AutocompleteMultiSelectOptionsHeader from './AutocompleteMultiSelectOptionsHeader';
import {
  AutocompleteMultiSelectItem,
  AutocompleteMultiSelectProps,
} from './types';

const useStyles = makeStyles((theme: JupiterTheme) =>
  createStyles({
    optionSubtitle: {
      marginTop: '-4px',
      color: theme.palette.grey[500],
      fontSize: '12px',
    },
    optionDecorationWrapper: {
      height: '100%',
      display: 'flex',
      alignItems: 'center',
    },
    optionContainer: {
      padding: theme.spacing(1),
    },
    label: {
      padding: theme.spacing(0, 0, 0.5),
      lineHeight: '1.5',
      display: 'block',
    },
    inputWrapperFocus: {
      borderColor: theme.palette.primary.main,
      boxShadow: '0 0 0 2px rgba(24, 144, 255, 0.2)',
    },
    inputWrapper: {
      border: `1px solid ${theme.palette.grey[400]}`,
      backgroundColor: theme.palette.common.white,
      borderRadius: '4px',
      padding: theme.spacing(0.125),
      display: 'flex',
      flexWrap: 'wrap',
      alignItems: 'center',
      '&:hover': {
        borderColor: theme.palette.primary.main,
      },
      '& input': {
        fontSize: '14px',
        height: '44px',
        boxSizing: 'border-box',
        padding: theme.spacing(0.5, 0.75),
        width: 0,
        minWidth: '30px',
        flexGrow: 1,
        border: 0,
        margin: 0,
        outline: 0,
        fontFamily: 'inherit',
      },
    },

    tagWrapper: {
      display: 'flex',
      alignItems: 'center',
      height: 40,
      margin: theme.spacing(0.25),
      lineHeight: '22px',
      backgroundColor: theme.palette.grey[50],
      border: `1px solid ${theme.palette.grey[400]}`,
      borderRadius: '2px',
      boxSizing: 'content-box',
      padding: theme.spacing(1),
      outline: 0,
      overflow: 'hidden',
      '&:focus': {
        borderColor: theme.palette.primary.main,
        backgroundColor: theme.palette.blue[50],
      },
      '& span': {
        overflow: 'hidden',
        whiteSpace: 'nowrap',
        textOverflow: 'ellipsis',
      },
      '& svg': {
        fontSize: '12px',
        cursor: 'pointer',
        padding: theme.spacing(0.5),
      },
    },
    smallIcon: {
      height: 18,
    },
    smallInput: {
      height: 44,
    },
    listbox: {
      width: '100%',
      margin: theme.spacing(0.25, 0, 0),
      padding: 0,
      position: 'absolute',
      listStyle: 'none',
      backgroundColor: theme.palette.common.white,
      overflow: 'auto',
      maxHeight: '250px',
      borderRadius: '4px',
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
      zIndex: 2,
      overflowX: 'hidden',
      '& li': {
        padding: theme.spacing(0.625, 1.5),
        display: 'flex',
        '& span': {
          flexGrow: 1,
        },
        '& svg': {
          color: 'transparent',
        },
      },

      "& li[aria-selected='true']": {
        backgroundColor: theme.palette.grey[50],
        fontWeight: 600,
        '& svg': {
          color: theme.palette.primary.light,
        },
      },

      "& li[data-focus='true']": {
        backgroundColor: theme.palette.blue[50],
        cursor: 'pointer',
        '& svg': {
          color: theme.palette.common.black,
        },
      },
    },
  })
);

const AutocompleteMultiSelect = <T extends AutocompleteMultiSelectItem>({
  className,
  classes: propsClasses = { root: undefined, combobox: undefined },
  id = 'autocomplete-multi-select',
  refetchable,
  onRefetch,
  loading,
  value,
  onChange,
  items,
  getDecorationForItem,
  label,
  disabled,
  oneLine,
}: AutocompleteMultiSelectProps<T>) => {
  const classes = { ...useStyles(), ...propsClasses };
  const {
    getRootProps,
    getInputLabelProps,
    getInputProps,
    getTagProps,
    getListboxProps,
    getOptionProps,
    groupedOptions,
    focused,
    setAnchorEl,
  } = useAutocomplete({
    id,
    value,
    onChange,
    multiple: true,
    options: items,
    filterOptions: (options, { inputValue }) => {
      return options.filter(
        (option) =>
          !value.some((value) => value.id === option.id) &&
          option.title.toLowerCase().includes(inputValue.toLowerCase())
      );
    },
    getOptionLabel: (option) => option.title,
  });

  const inputProps = getInputProps() as { value: string; [key: string]: any };
  return (
    <div className={clsx(className, classes.root)}>
      <div {...getRootProps()} className={classes.combobox}>
        {label && (
          <label className={classes.label} {...getInputLabelProps()}>
            {label}
          </label>
        )}
        <div
          ref={setAnchorEl}
          className={clsx(
            classes.inputWrapper,
            focused && classes.inputWrapperFocus
          )}
        >
          {value.map((option, index) => {
            const decoration = getDecorationForItem?.(option);
            return (
              <div
                className={clsx(
                  classes.tagWrapper,
                  oneLine && classes.smallIcon
                )}
                key={`${option.id}`}
              >
                {decoration && (
                  <div className={classes.optionDecorationWrapper}>
                    {decoration}
                  </div>
                )}
                <div className={classes.optionContainer}>
                  {option.title && <div>{option.title}</div>}
                  {option.subtitle && (
                    <div className={classes.optionSubtitle}>
                      {option.subtitle}
                    </div>
                  )}
                  {!oneLine
                    ? option.id && (
                        <div className={classes.optionSubtitle}>
                          {option.id}
                        </div>
                      )
                    : null}
                </div>
                <CloseIcon onClick={(getTagProps({ index }) as any).onDelete} />
              </div>
            );
          })}
          <input {...inputProps} disabled={disabled} />
        </div>
      </div>
      {groupedOptions.length > 0 && (
        <ul {...getListboxProps()} className={classes.listbox}>
          {!oneLine ? (
            <AutocompleteMultiSelectOptionsHeader
              refetchable={refetchable}
              onRefetch={onRefetch}
              loading={loading}
            />
          ) : null}
          {groupedOptions.map((option, index) => (
            <li
              {...getOptionProps({ option, index })}
              className={classes.optionDecorationWrapper}
              key={index}
            >
              {getDecorationForItem?.(option)}
              <div className={classes.optionContainer}>
                {option.title && <div>{option.title}</div>}
                {option.subtitle && (
                  <div className={classes.optionSubtitle}>
                    {option.subtitle}
                  </div>
                )}
                {!oneLine
                  ? option.id && (
                      <div className={classes.optionSubtitle}>{option.id}</div>
                    )
                  : null}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default AutocompleteMultiSelect;
